/*
 * Copyright (c) 2025. Bolmate B.V.
 */
import './style/notificationsPage.scss';

import React, {MouseEventHandler, useContext, useState} from 'react';
import {AppContext} from '../misc/appContext';
import {SubscriptionModuleName, SubscriptionModuleStatus} from '../../util/enums';
import {AwesomeIcon, Loader} from '@dvrd/dvr-controls';
import Notification from "../../models/notificationModel";
import ReviewNotifications from "./reviewNotifications/reviewNotifications";
import BuyBoxNotifications from "./buyBoxNotifications/buyBoxNotifications";
import StockNotifications from "./stockNotifications/stockNotifications";
import OrderNotifications from "./orderNotification/orderNotifications";
import ProductHijackNotifications from "./productHijackNotification/productHijackNotification";
import IDate from "@dvrd/idate";
import InfoBanner from "../misc/infoBanner";

export interface NotificationTypeProps {
    onChangeNotification: (notification: Notification) => void;
    onReplaceNotification: (notification: Notification) => void;
    onDeleteNotification: (notification: Notification) => MouseEventHandler;
    onSendTestNotification: (notification: Notification) => MouseEventHandler;
    notifications: Array<Notification>;
    loading: boolean;
    moduleActive: boolean;
    canEdit: boolean;
    canDelete: boolean;
}

export interface NotificationProps extends NotificationTypeProps {
    notification: Notification;
    disabled: boolean;
}

interface Props extends NotificationTypeProps {
    onActivateModule: MouseEventHandler;
    onClickAdd: MouseEventHandler;
}

export default function NotificationsPage(props: Props) {
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const {
        onActivateModule,
        loading,
        onSendTestNotification,
        notifications,
        moduleActive,
        onChangeNotification,
        onDeleteNotification,
        onClickAdd,
        canDelete,
        canEdit,
        onReplaceNotification
    } = props;
    const [bannerButtonDismissed, setBannerButtonDismissed] = useState(false);

    function renderBanner() {
        const module = user?.subscription?.getModule(SubscriptionModuleName.NOTIFICATIONS);

        if (bannerButtonDismissed) return null;
        // There are 4 possible combinations:
        // 1. Status = TRIAL (trialing without automatic activation): show banner with full text and CTA
        // 2. Status = PAID and charge_per date is after today (so still trialing,
        //  but will activate automatically after trial): show banner without trial text and without CTA
        // 3. Status = PAID and charge_per is at or before today (actively paying): Show no banner
        // 4. Status !== TRIAL && !== PAID (canceled or canceling module): Show banner without trial text, but with CTA
        const now = IDate.now();
        const isTrial = module?.status === SubscriptionModuleStatus.TRIAL;
        const isPaid = module?.status === SubscriptionModuleStatus.PAID;
        const isPaidActive = isPaid && module?.chargePerDate?.isAtOrBefore(now, 'day');
        const isPaidFuture = isPaid && module?.chargePerDate?.isAfter(now, 'day');
        const isCanceledOrCanceling =
            module?.status !== SubscriptionModuleStatus.TRIAL && module?.status !== SubscriptionModuleStatus.PAID;

        // 3. Actively paying - no banner
        if (isPaidActive) return null;

        let showTrialText = false;
        let showCTA = false;

        if (isTrial) {
            // 1. TRIAL
            showTrialText = true;
            showCTA = true;
        } else if (isPaidFuture) {
            // 2. PAID, activates in future
            showTrialText = false;
            showCTA = false;
        } else if (isCanceledOrCanceling) {
            // 4. Canceled / canceling
            showTrialText = false;
            showCTA = true;
        }

        let message = `Met deze feature ontvang je de belangrijkste meldingen direct in jouw inbox.
             Bijvoorbeeld bij een koopblok verlies of een lage voorraad.
             Zo ben je altijd up-to-date, zodat jij actie kunt ondernemen en geen omzet verliest.
             
             Vanwege de extra serverkosten voor 24/7 monitoring, kost een deel van de notificaties extra.
             Voor een totale meerprijs van +€5 p/m krijg je alle notificaties voor al jouw verkoopaccount inbegrepen.`;

        if (showTrialText) message += `\nTijdens de proef, kun je het kosteloos blijven proberen.`;

        const handleActivate: MouseEventHandler = e => {
            setBannerButtonDismissed(true);
            onActivateModule(e);
        };

        return (
            <InfoBanner
                persistDismiss={false}
                primaryBannerButton={true}
                message={message}
                actionLabel={showCTA ? 'Feature activeren voor bovenstaande meerprijs' : undefined}
                onAction={showCTA ? handleActivate : undefined}
            />
        );
    }

    function renderAddButton() {
        return (
            <div className='btn-add' onClick={onClickAdd}>
                <div className='plus-circle'>
                    <AwesomeIcon name='plus' className='plus-icon' />
                </div>
                <label className='plus-label'>Voeg een e-mail notificatie toe</label>
            </div>
        );
    }

    return (
        <>
            <InfoBanner
                featureKey='notifications'
                variant='info'
                title='Notificaties'
                message='Ontvang notificaties per e-mail bij belangrijke events, zodat je op de hoogte bent en eventueel actie kunt nemen.'
                showForNewUsersOnly={true}
            />
            {renderBanner()}
            <div className='notifications-page'>
                <Loader active={loading} />
                <div className='notifications-header-container'>
                    <label className='notifications-title'>Notificaties</label>
                    <label className='sub-title'>Ontvang een melding per e-mail bij onderstaande triggers:</label>
                </div>
                <ReviewNotifications
                    notifications={notifications}
                    loading={loading}
                    moduleActive={moduleActive}
                    canDelete={canDelete}
                    canEdit={canEdit}
                    onChangeNotification={onChangeNotification}
                    onDeleteNotification={onDeleteNotification}
                    onReplaceNotification={onReplaceNotification}
                    onSendTestNotification={onSendTestNotification}
                />
                <BuyBoxNotifications
                    onChangeNotification={onChangeNotification}
                    onDeleteNotification={onDeleteNotification}
                    onSendTestNotification={onSendTestNotification}
                    onReplaceNotification={onReplaceNotification}
                    notifications={notifications}
                    loading={loading}
                    moduleActive={moduleActive}
                    canEdit={canEdit}
                    canDelete={canDelete}
                />
                <StockNotifications
                    onChangeNotification={onChangeNotification}
                    onDeleteNotification={onDeleteNotification}
                    onSendTestNotification={onSendTestNotification}
                    onReplaceNotification={onReplaceNotification}
                    notifications={notifications}
                    loading={loading}
                    moduleActive={moduleActive}
                    canEdit={canEdit}
                    canDelete={canDelete}
                />
                <ProductHijackNotifications
                    onChangeNotification={onChangeNotification}
                    onDeleteNotification={onDeleteNotification}
                    onSendTestNotification={onSendTestNotification}
                    onReplaceNotification={onReplaceNotification}
                    notifications={notifications}
                    loading={loading}
                    moduleActive={moduleActive}
                    canEdit={canEdit}
                    canDelete={canDelete}/>
                <OrderNotifications
                    onChangeNotification={onChangeNotification}
                    onDeleteNotification={onDeleteNotification}
                    onSendTestNotification={onSendTestNotification}
                    onReplaceNotification={onReplaceNotification}
                    notifications={notifications}
                    loading={loading}
                    moduleActive={moduleActive}
                    canEdit={canEdit}
                    canDelete={canDelete}
                />
                {renderAddButton()}
            </div>
        </>
    );
}
